// ============================================================
// MySQLAssignments.jsx
//
// MySQL Capstone Assignment
// Covers Full MySQL Curriculum + DBA Responsibilities
// Foundation · Module 12
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
} from "@mui/material";

/* Icons */
import AssignmentIcon from "@mui/icons-material/Assignment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StorageIcon from "@mui/icons-material/Storage";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ============================================================
   MAIN COMPONENT
============================================================ */
export default function MySQLAssignments() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 12 · Capstone"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    MySQL <span style={{ color: BRAND.gold }}>Capstone Assignment</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 820 }}>
                    This assignment evaluates your ability to design, secure, optimize,
                    and operate a real‑world MySQL database. It reflects what a
                    <strong> professional Database Administrator or Backend Developer </strong>
                    is expected to do in production.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* OVERVIEW */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" sx={{ ...sectionHeading, mb: 2 }}>
                        📌 Assignment Overview
                    </Typography>

                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                            You are hired as a <strong style={{ color: BRAND.white }}>Database Engineer</strong>
                            for an online learning platform called <strong style={{ color: BRAND.gold }}>AcademyPro</strong>.
                            Your task is to design, implement, secure, and optimize the MySQL database
                            that supports students, courses, enrollments, and payments.
                        </Typography>
                    </Paper>
                </Box>

                {/* REQUIREMENTS */}
                <Typography variant="h5" sx={{ ...sectionHeading, mb: 3 }}>
                    ✅ Assignment Requirements
                </Typography>

                <Grid container spacing={3}>

                    {/* SCHEMA DESIGN */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                1️⃣ Database & Table Design
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Create a database called <code>academy_pro</code><br />
                                • Design tables: users, courses, enrollments, payments<br />
                                • Use correct data types and constraints<br />
                                • Apply primary keys and foreign keys<br />
                                • Normalize data properly (no duplication)
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* CRUD */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                2️⃣ CRUD Operations
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Insert sample users, courses, enrollments, payments<br />
                                • Write SELECT queries with filters and sorting<br />
                                • Update user and payment records safely<br />
                                • Delete records using transactions
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* JOINS */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                3️⃣ Joins & Relationships
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • INNER JOIN students with enrollments<br />
                                • LEFT JOIN courses with enrollments<br />
                                • Many‑to‑many relationship via junction table<br />
                                • Use aliases and readable SQL
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* FUNCTIONS */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                4️⃣ Filtering, Sorting & Functions
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Use WHERE, ORDER BY, LIMIT<br />
                                • Use COUNT, SUM, AVG for reports<br />
                                • Group results with GROUP BY & HAVING
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* SUBQUERIES */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                5️⃣ Subqueries & Views
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Use subqueries to filter top students<br />
                                • Create reusable views for reports<br />
                                • Query views like normal tables
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* INDEXING */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                6️⃣ Indexes & Optimization
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Create indexes on foreign keys<br />
                                • Use EXPLAIN to analyze queries<br />
                                • Identify slow queries
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* TRANSACTIONS */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                7️⃣ Transactions & ACID
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Wrap payments in transactions<br />
                                • Demonstrate COMMIT & ROLLBACK<br />
                                • Explain ACID properties
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* SECURITY */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                8️⃣ Security & User Management
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Create app_user and report_user<br />
                                • Grant least privilege access<br />
                                • Revoke dangerous permissions
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* STORED LOGIC */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                9️⃣ Stored Procedures & Triggers
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Stored procedure for enrollment<br />
                                • Trigger to prevent invalid payments<br />
                                • Audit log trigger
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* APPLICATION */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1 }}>
                                🔟 MySQL with Applications
                            </Typography>
                            <Typography sx={{ color: BRAND.muted }}>
                                • Show sample JPA entity + repository<br />
                                • Show Node.js prepared query<br />
                                • Use environment variables
                            </Typography>
                        </Paper>
                    </Grid>

                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* SUBMISSION */}
                <Typography variant="h5" sx={{ ...sectionHeading, mb: 2 }}>
                    📤 Submission Guidelines
                </Typography>

                <Typography sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                    • Submit a <strong>single SQL file</strong> containing schema + queries<br />
                    • Include comments explaining your decisions<br />
                    • Use meaningful naming conventions<br />
                    • No hard‑coded passwords in code examples
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        🏆 <strong>Capstone Goal:</strong> If you can complete this assignment
                        confidently, you are operating at a **junior‑to‑mid Database Administrator /
                        Backend Engineer level**.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
